import React, { useState } from 'react';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Mail, Lock, ArrowUpRight, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { heroImages } from '../mock/data';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const { login, user } = useAuth();

  if (user) return <Navigate to={loc.state?.from || '/account'} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await login(email, password);
      nav(loc.state?.from || '/account');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Login failed. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={heroImages.counter} alt="Lanka Express" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/70 to-black/40" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Members</span></div>
          <h2 className="font-serif-display text-5xl font-bold leading-[1.05] mb-4">Welcome back to<br /><em className="text-[#c8102e] not-italic">Lanka Express</em>.</h2>
          <p className="text-white/70 max-w-md">Access your account to view shipments, manage your profile and enjoy premium member-only benefits.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-14 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-3"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Sign In</span></div>
          <h1 className="font-serif-display text-4xl font-bold text-black mb-8">Access your account</h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-11 h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-11 h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
              </div>
            </div>
            {err && <div className="flex items-center gap-2 text-sm text-[#c8102e]"><AlertCircle className="w-4 h-4" /> {err}</div>}
            <Button type="submit" disabled={busy} className="bg-[#c8102e] hover:bg-black text-white h-12 w-full font-semibold tracking-[0.15em] uppercase text-[13px]">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Sign In
            </Button>
          </form>
          <p className="mt-8 text-sm text-neutral-600">
            New to Lanka Express?{' '}
            <Link to="/signup" className="text-[#c8102e] font-semibold hover:underline inline-flex items-center gap-1">Create an account <ArrowUpRight className="w-3 h-3" /></Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
